import { AbstractDocumentManager } from 'app/services/rtc/sync/documentmanagers/base';
import { ActionState, Events, IDocumentManager, DummyDocumentManager } from 'app/services/rtc/sync/documentmanagers';
import { IEncodedPersistedAction, StreamPosition, makeStreamPosition, PersistedActionCodec } from '@engoo/realtime-protocol/persisted';
import { ActionStreamEvents, IActionStream, IMetadata } from 'app/services/rtc/sync/ActionStream';
import { Map, List } from 'immutable';
import { decodeOrThrow } from 'app/utils/ioTs';
import { ConditionVariable } from 'app/utils/ConditionVariable';
import { sleep } from 'app/utils/async';
import { CancelToken, rejectOnCancel } from 'app/utils/cancelToken';
import RealtimeError from 'app/services/rtc/RealtimeError';
import { notify } from 'app/services/errorReporter';

export interface ICompressionResult<ActionT> {
  /**
   * Result of compression
   *
   * If those actions compressed to a no-op, this field should
   * be undefined.
   */
  action?: ActionT;

  /**
   * Amount of actions that the `action` compressed to
   */
  compressed: number;
}

export interface ILazyActionHandler<DocumentT, ActionT> {
  /**
   * Applies an action on top of current document state
   */
  apply(document: DocumentT, x: ActionT, state: ActionState): DocumentT;
  /**
   * Called on all queued actions before sending
   *
   * @returns `Iterable` of `ICompressionResult`
   */
  compress(document: DocumentT, actions: Iterable<ActionT>): Iterable<ICompressionResult<ActionT>>;
}

export const DEFAULT_INTERVAL = 300;

export interface ILazyDocumentManager<DocumentT, ActionT>
extends IDocumentManager<DocumentT, ActionT> {
  addLocalAction(action: ActionT): void;
}

export class DummyLazyDocumentManager<DocumentT, ActionT>
extends DummyDocumentManager<DocumentT, ActionT>
implements ILazyDocumentManager<DocumentT, ActionT> {
  public addLocalAction() {}
}

/**
 * Queues actions and only send them with 300ms between each send
 */
export class LazyDocumentManager<DocumentT, ActionT>
extends AbstractDocumentManager<DocumentT, ActionT>
implements ILazyDocumentManager<DocumentT, ActionT> {
  private readonly loopSignal = new ConditionVariable();
  private stopLoop = () => {};

  /**
   * List of all new actions as they come from `addLocalActions`, uncompressed
   */
  private freshActions: List<ActionT> = List();

  /**
   * Map of persisted action keyed by `position`, potentially coming
   * out of order
   */
  private aheadActions: Map<StreamPosition, ActionT & IEncodedPersistedAction> = Map();

  public constructor(
    private readonly actionHandler: ILazyActionHandler<DocumentT, ActionT>,
    actionStream: IActionStream<ActionT>,
    snapshot: DocumentT,
    metadata: IMetadata | undefined,
    snapshotVersion: number = -1,
    private readonly refreshInterval: number = DEFAULT_INTERVAL,
  ) {
    super(actionStream, snapshot, metadata, snapshotVersion);
  }

  public start() {
    super.start();

    // Don't store the cancel token on the class. If we did that the
    // `sendLoop` could access the new token when the manager is
    // restared. This would result in the loop started previously
    // to continue to run.
    const loopToken = CancelToken.create();
    this.stopLoop = loopToken.cancel;
    this.sendLoop(loopToken.cancelToken);

    this.actionStream.on(ActionStreamEvents.ACTION, this.onAction);
  }

  public shutdown() {
    this.actionStream.removeListener(ActionStreamEvents.ACTION, this.onAction);
    this.stopLoop();
    super.shutdown();
  }

  public readonly onActions = (actions: Array<ActionT & IEncodedPersistedAction>) => {
    this.aheadActions = actions.reduce((ahead, action) => {
      const { position } = decodeOrThrow(PersistedActionCodec, action);
      return ahead.set(position, action);
    }, this.aheadActions);

    this.integrateActions();
  };

  public readonly onAction = (action: ActionT & IEncodedPersistedAction) => {
    this.onActions([action]);
  };

  public addLocalAction(action: ActionT) {
    this.freshActions = this.freshActions.push(action);
    this.integrateActions();
    this.loopSignal.signal();
  }

  private async sendLoop(cancelToken: CancelToken) {
    while (!cancelToken.isCancelled()) {
      try {
        // Wait for new fresh actions
        if (this.freshActions.size === 0) {
          await Promise.race([
            this.loopSignal.wait(),
            rejectOnCancel(cancelToken),
          ]);
        }
        // Wait to accumulate more actions
        await sleep(this.refreshInterval, cancelToken);

        await this.sendActions(cancelToken);
      } catch (error) {
        if (!cancelToken.isCancelled()) {
          this.freshActions = List();

          if (!(error instanceof RealtimeError)) {
            notify(error, { name: 'LazyDocumentManager' });
          }
        }
      }
    }
  }

  private async sendActions(cancelToken: CancelToken) {
    const compressedActions = this.actionHandler.compress(this.snapshot, this.freshActions);

    for (const { action, compressed } of compressedActions) {
      if (cancelToken.isCancelled()) {
        return;
      }

      // Send action to remote if it wasn't reduced to a no-op
      // during compression
      const persisted = action && await this.actionStream.append(action);

      // Remove the amount of original actions that the current
      // action was compressed from
      this.freshActions = this.freshActions.skip(compressed);

      if (persisted) {
        this.onAction(persisted);
      }
    }
  }

  /**
   * Integrate all position-continguous `aheadActions` to `snapshot`
   * and all pending actions to `latestSnapshot`
   */
  private integrateActions() {
    // Integrate continguous persisted actions to `snapshot`
    for (const [position, action] of this.continguousAheadActions()) {
      this.snapshot = this.actionHandler.apply(this.snapshot, action, ActionState.Confirmed);
      this.snapshotVersion = position;
      this.aheadActions = this.aheadActions.delete(position);
    }

    // Base snapshot with pending actions applied off of `snapshot`
    this.latestSnapshot = this.snapshot;

    // Integrate all non-continguous aheadActions to a temporary snapshot
    for (const action of this.noncontinguousAheadActions()) {
      this.latestSnapshot = this.actionHandler.apply(this.latestSnapshot, action, ActionState.ConfirmedOutOfOrder);
    }

    // Integrate all pending actions to a temporary snapshot
    for (const action of this.freshActions) {
      this.latestSnapshot = this.actionHandler.apply(this.latestSnapshot, action, ActionState.Pending);
    }

    this.emit(Events.CHANGED, {
      actions: [],
      state: this.latestSnapshot,
    });
  }

  /**
   * Return iterable of all `adheadActions` that have `position`
   * incrementing continuously.
   */
  private *continguousAheadActions(): Iterable<[StreamPosition, ActionT & IEncodedPersistedAction]> {
    for (
      let position = makeStreamPosition(this.snapshotVersion + 1),
        action = this.aheadActions.get(position);
      action;
      position = makeStreamPosition(position + 1),
      action = this.aheadActions.get(position)
    ) {
      yield [position, action];
    }
  }

  /**
   * Return iterable of non-continuous `aheadActions`, in other
   * words, `aheadActions` that are newer than the `snapshotVersion`
   * but their `position` doesn't increase continuously.
   */
  private *noncontinguousAheadActions() {
    yield* this.aheadActions
      .filter(({ position }) => position > this.snapshotVersion)
      .sortBy(({ position }) => position)
      .valueSeq();
  }
}
